import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import clsx from 'clsx';

import ActionCard, { ActionCardSize, ActionCardVariant } from 'components/Common/Cards/ActionCard';
import BottomSheet from 'components/Common/BottomSheet';
import MoreLink from 'components/MoreLink';
import { getLoanGoal } from 'selectors/getLoanGoal';
import { RoutePath } from 'enums/Routes';
import { useNavigate } from 'hooks/useNavigate';
import { ReactComponent as ClockIcon } from 'images/clock-icon.svg';
import { ReactComponent as UpwardTrendIcon } from 'images/upward-trend-icon.svg';

import AvailableShiftsSection from 'components/FinancialCheckup/FinancialDashboard/AvailableShiftsSection';
import { useAvailableShifts } from 'components/FinancialCheckup/FinancialDashboard/AvailableShiftsSection/useAvailableShifts';

import styles from './DashboardActionTiles.module.scss';

const SHIFT_TILE_CLICKED_EVENT = 'Dashboard ShiftIQ Tile Clicked';

const DashboardActionTiles: React.FC = () => {
  const [isShiftsSheetOpen, setIsShiftsSheetOpen] = useState(false);
  const { shifts } = useAvailableShifts();
  const { plaidConnected, isLoading: isLoanGoalLoading } = useSelector(getLoanGoal);
  const navigate = useNavigate();

  const showShiftsTile = Boolean(shifts?.length);
  const showConnectTile = !plaidConnected && !isLoanGoalLoading;

  if (!showShiftsTile && !showConnectTile) return null;

  const handleOpenShiftsSheet = () => {
    analytics.track(SHIFT_TILE_CLICKED_EVENT);
    setIsShiftsSheetOpen(true);
  };

  const handleConnectAccounts = () => {
    analytics.track('Dashboard Connect Accounts Clicked');
    navigate(RoutePath.PlaidCheckingAccounts);
  };

  return (
    <>
      <div className={styles.row}>
        {showShiftsTile && (
          <ActionCard
            variant={ActionCardVariant.White}
            size={ActionCardSize.Compact}
            onClick={handleOpenShiftsSheet}
            className={clsx(styles.tile, { [styles.tileFull]: !showConnectTile })}
            contentClassName={styles.tileContent}
          >
            <div className={styles.iconCircle}>
              <ClockIcon />
            </div>
            <ActionCard.Title className={styles.tileTitle}>Plannery ShiftIQ</ActionCard.Title>
            <ActionCard.Description className={styles.tileDescription}>
              Every additional shift can accelerate your path towards your goals
            </ActionCard.Description>
            <MoreLink text="Learn more" onClick={() => undefined} className={styles.cta} />
          </ActionCard>
        )}

        {showConnectTile && (
          <ActionCard
            variant={ActionCardVariant.White}
            size={ActionCardSize.Compact}
            onClick={handleConnectAccounts}
            className={clsx(styles.tile, { [styles.tileFull]: !showShiftsTile })}
            contentClassName={styles.tileContent}
          >
            <div className={styles.iconCircle}>
              <UpwardTrendIcon />
            </div>
            <ActionCard.Title className={styles.tileTitle}>Stay on track</ActionCard.Title>
            <ActionCard.Description className={styles.tileDescription}>
              Connect your accounts and we will help you stay on track
            </ActionCard.Description>
            <MoreLink text="Connect accounts" onClick={() => undefined} className={styles.cta} />
          </ActionCard>
        )}
      </div>

      <BottomSheet isOpen={isShiftsSheetOpen} onClose={() => setIsShiftsSheetOpen(false)}>
        <AvailableShiftsSection onClose={() => setIsShiftsSheetOpen(false)} />
      </BottomSheet>
    </>
  );
};

export default DashboardActionTiles;
